import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { connect } from 'react-redux';
import { useState } from 'react';

import { KnmCollapse } from 'KnmPackage/MainModule/components/KnmCollapse';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { IKnmErknmForm } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmForm';
import { IKnmErknmFormResultDecision } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormResultDecision';
import { IKnmErknmFormResponsibleEntitie } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormResponsibleEntitie';
import { getKnmDataCreated } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { Button } from 'UIPackage/MainModule/components/Button';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { TextareaField } from 'UIPackage/MainModule/components/TextareaField';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { SelectInputFieldAutoOptions } from 'UIPackage/MainModule/components/SelectInputFieldAutoOptions';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IAdminSetting } from 'APIPackage/AdminModule/interfaces/IAdminSetting';
import { KnmResponsibleTypeCodesEnum } from 'APIPackage/CatalogsModule/enums/KnmResponsibleTypeCodesEnum';
import { ResponsibilityTypeCodesEnum } from 'APIPackage/CatalogsModule/enums/ResponsibilityTypeCodesEnum';
import { IKnmErknmFormStructuresNPA } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormStructuresNPA';
import { IKnmResponsibileTypeSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmResponsibileTypeSelectOption';

import styles from './ErknmResultDecisionsItem.module.scss';
import { IKnmErknmFormAct } from '../../MainModule/interfaces/IKnmErknmFormAct';
import { IKnmErknmFormEvent } from '../../MainModule/interfaces/IKnmErknmFormEvent';
import { IKnmErknmFormSubject } from '../../MainModule/interfaces/IKnmErknmFormSubject';
import { ErknmResultDecisionResponsibleEntitiesItem } from './ErknmResultDecisionResponsibleEntitiesItem';
import { KnmLateFieldsRemark } from '../../../KnmPackage/KnmLateFieldsRemarkModule/components/KnmLateFieldsRemark';
import { checkHasErrorInCollapseBlock } from '../../../functions/checkHasErrorInCollapseBlock';

interface IErknmResultDecisionsResponsibleEntitiesProps {
  acts: IKnmErknmFormAct[] | undefined;
  addField: (fieldName: string) => () => void;
  eventsErknm?: IKnmErknmFormEvent[] | undefined;
  formRef: IKnmFormRef | undefined;
  id: string;
  isWatchOnly: boolean;
  lateFieldName: string;
  makeRemoveField: (fieldName: string, index: number) => () => void;
  name: string;
  organizations?: IKnmErknmFormSubject[] | undefined;
  value: IKnmErknmFormResultDecision;
}

interface IErknmResultDecisionsResponsibleEntitiesStoreProps {
  created: string | null | undefined;
  settings: IAdminSetting[] | undefined;
}

const ErknmResultDecisionsResponsibleEntitiesWithoutStore = ({
  acts,
  addField,
  eventsErknm,
  formRef,
  id,
  isWatchOnly,
  lateFieldName,
  makeRemoveField,
  name,
  organizations,
  value,
  created,
  settings,
}: IErknmResultDecisionsResponsibleEntitiesProps & IErknmResultDecisionsResponsibleEntitiesStoreProps) => {
  const [knmCollapseOpened, setKnmCollapseOpened] = useState<{ [key: string]: boolean }>({});
  const { responsibleEntities } = value;
  const responsibleEntitiesBlockId = `${id}.responsibleEntities`;

  const isUse4127 = isLaterThanAdminSetting(created, settings, AdminSettingCodesEnum.KNM_VALIDATION_4_1_27_STARTS_FROM);

  const handleTypeChange = (fieldName: string) => () => {
    formRef?.current.change(`${fieldName}.inn` as keyof IKnmErknmForm);
    formRef?.current.change(`${fieldName}.ogrn` as keyof IKnmErknmForm);
    formRef?.current.change(`${fieldName}.snils` as keyof IKnmErknmForm);
    formRef?.current.change(`${fieldName}.fullName` as keyof IKnmErknmForm);
    formRef?.current.change(`${fieldName}.organizationName` as keyof IKnmErknmForm);
  };

  const getFilterResponsibleEntitiesSubjectTypeSelectOptions = (arr: IKnmResponsibileTypeSelectOption[] | undefined) =>
    arr?.filter(selectOption => selectOption.enabledBefore4127);

  return (
    <FieldArray name={`${name}.responsibleEntities`}>
      {({ fields }: FieldArrayRenderProps<IKnmErknmFormResponsibleEntitie, HTMLElement>) => {
        if (!fields.length) {
          return null;
        }

        return fields.map((fieldName, index) => {
          const fieldValue: IKnmErknmFormResponsibleEntitie = fields.value?.[index];
          const responsibleEntityId = `${id}.responsibleEntities[${fieldValue.listId}]`;

          const hasError = checkHasErrorInCollapseBlock(formRef, fieldName);

          if (isUse4127) {
            return (
              <KnmCollapse
                theme="fields"
                title={`Ответственное лицо №${index + 1}`}
                id={responsibleEntityId}
                key={responsibleEntityId}
                opened={true}
                onClose={makeRemoveField('responsibleEntities', index)}
                headerPostfix={
                  <KnmLateFieldsRemark lateFieldPath={`${lateFieldName}/${fieldValue.listId}`} isLeftPosition={true} />
                }
                changeOpenedCallback={e =>
                  setKnmCollapseOpened(state => ({
                    ...state,
                    [fieldName]: e,
                  }))
                }
                error={hasError && knmCollapseOpened[fieldName]}
              >
                <ErknmResultDecisionResponsibleEntitiesItem
                  fieldValue={fieldValue}
                  responsibleEntitiesItemName={fieldName}
                  eventsErknm={eventsErknm}
                  acts={acts}
                  organizations={organizations}
                  isWatchOnly={isWatchOnly}
                  fields={fields}
                  responsibleEntitiesItemIndex={index}
                  name={name}
                  id={responsibleEntityId}
                  addField={addField}
                  formRef={formRef}
                  responsibleEntitiesBlockId={responsibleEntitiesBlockId}
                  lateFieldName={`${lateFieldName}/${fieldValue.listId}`}
                />
              </KnmCollapse>
            );
          }

          const currentResponsibilityCode = fieldValue.responsibilityType?.code;
          const isRequiredPosition =
            responsibleEntities &&
            responsibleEntities[index].subjectType?.code === KnmResponsibleTypeCodesEnum.OFFICIAL;

          return (
            <Row key={responsibleEntityId} isListItem margin="medium">
              <Row notWrap>
                <FieldBlock margin="right" id={`${responsibleEntityId}.subjectType`}>
                  <SelectInputFieldAutoOptions
                    name={`${fieldName}.subjectType`}
                    placeholder="Выберите тип"
                    size="medium"
                    optionName={CommonCatalogsNamesEnum.KNM_RESPONSIBLE_TYPES}
                    disabled={isWatchOnly}
                    isClearable={false}
                    onChange={handleTypeChange(fieldName)}
                    transformFunc={getFilterResponsibleEntitiesSubjectTypeSelectOptions}
                  />
                </FieldBlock>
                <KnmLateFieldsRemark
                  lateFieldPath={`${lateFieldName}/${fieldValue.listId}/subjectType/name`}
                  fieldId={`${responsibleEntityId}.subjectType`}
                  isLeftPosition={true}
                />
                {responsibleEntities &&
                responsibleEntities[index].subjectType?.code === KnmResponsibleTypeCodesEnum.ULIP ? (
                  <>
                    <FieldBlock margin="right" id={`${responsibleEntityId}.inn`}>
                      <TextareaField name={`${fieldName}.inn`} disabled={isWatchOnly} placeholder="ИНН" />
                    </FieldBlock>
                    <KnmLateFieldsRemark
                      lateFieldPath={`${lateFieldName}/${fieldValue.listId}/inn`}
                      fieldId={`${responsibleEntityId}.inn`}
                      isLeftPosition={true}
                    />
                    <FieldBlock margin="right" id={`${responsibleEntityId}.ogrn`}>
                      <TextareaField name={`${fieldName}.ogrn`} disabled={isWatchOnly} placeholder="ОГРН" />
                    </FieldBlock>
                    <KnmLateFieldsRemark
                      lateFieldPath={`${lateFieldName}/${fieldValue.listId}/ogrn`}
                      fieldId={`${responsibleEntityId}.ogrn`}
                      isLeftPosition={true}
                    />
                    <FieldBlock id={`${responsibleEntityId}.organizationName`}>
                      <TextareaField
                        name={`${fieldName}.organizationName`}
                        disabled={isWatchOnly}
                        placeholder="Наименование"
                      />
                    </FieldBlock>
                    <KnmLateFieldsRemark
                      lateFieldPath={`${lateFieldName}/${fieldValue.listId}/organizationName`}
                      fieldId={`${responsibleEntityId}.organizationName`}
                      isLeftPosition={true}
                    />
                  </>
                ) : (
                  <>
                    <FieldBlock margin="right" id={`${responsibleEntityId}.fullName`}>
                      <TextareaField name={`${fieldName}.fullName`} disabled={isWatchOnly} placeholder="ФИО" />
                    </FieldBlock>
                    <KnmLateFieldsRemark
                      lateFieldPath={`${lateFieldName}/${fieldValue.listId}/fullName`}
                      fieldId={`${responsibleEntityId}.fullName`}
                      isLeftPosition={true}
                    />
                    <FieldBlock margin="right" id={`${responsibleEntityId}.snils`}>
                      <TextareaField name={`${fieldName}.snils`} disabled={isWatchOnly} placeholder="СНИЛС" />
                    </FieldBlock>
                    <KnmLateFieldsRemark
                      lateFieldPath={`${lateFieldName}/${fieldValue.listId}/snils`}
                      fieldId={`${responsibleEntityId}.snils`}
                      isLeftPosition={true}
                    />
                    {isRequiredPosition && (
                      <>
                        <FieldBlock id={`${responsibleEntityId}.position`}>
                          <TextareaField
                            name={`${fieldName}.position`}
                            disabled={isWatchOnly}
                            placeholder="Должность"
                          />
                        </FieldBlock>
                        <KnmLateFieldsRemark
                          lateFieldPath={`${lateFieldName}/${fieldValue.listId}/position`}
                          fieldId={`${responsibleEntityId}.position`}
                          isLeftPosition={true}
                        />
                      </>
                    )}
                  </>
                )}
                <CloseButton
                  className="shared-deleting-row-btn"
                  onClick={makeRemoveField('responsibleEntities', index)}
                />
              </Row>

              <Row title="Сведения о виде назначенного наказания" notWrap>
                <SelectInputFieldAutoOptions
                  className={styles.AssignedPunishmentNoteSelectInput}
                  id={`${responsibleEntityId}.responsibilityType`}
                  name={`${fieldName}.responsibilityType`}
                  placeholder="Выберите вид наказания"
                  size="medium"
                  optionName={CommonCatalogsNamesEnum.RESPONSIBILITY_TYPES}
                  disabled={isWatchOnly}
                  isClearable={false}
                />
                <KnmLateFieldsRemark
                  lateFieldPath={`${lateFieldName}/${fieldValue.listId}/responsibilityType/name`}
                  fieldId={`${responsibleEntityId}.responsibilityType`}
                  isLeftPosition={true}
                />
              </Row>

              {currentResponsibilityCode === ResponsibilityTypeCodesEnum.RSP_TYPE_I && (
                <Row title="Сведения о размере наказания" notSpace notWrap>
                  <FieldBlock size="col-4" margin="right" id={`${responsibleEntityId}.punishmentAmount`}>
                    <TextInputField name={`${fieldName}.punishmentAmount`} placeholder="Сумма" disabled={isWatchOnly} />
                  </FieldBlock>
                  <KnmLateFieldsRemark
                    lateFieldPath={`${lateFieldName}/${fieldValue.listId}/punishmentAmount`}
                    fieldId={`${responsibleEntityId}.punishmentAmount`}
                    isLeftPosition={true}
                  />
                  <FieldBlock size="col-4" id={`${responsibleEntityId}.punishmentAmountMeasure`}>
                    <TextInputField
                      name={`${fieldName}.punishmentAmountMeasure`}
                      placeholder="Ед. измерения"
                      disabled={isWatchOnly}
                    />
                  </FieldBlock>
                  <KnmLateFieldsRemark
                    lateFieldPath={`${lateFieldName}/${fieldValue.listId}/punishmentAmountMeasure`}
                    fieldId={`${responsibleEntityId}.punishmentAmountMeasure`}
                    isLeftPosition={true}
                  />
                </Row>
              )}

              {(currentResponsibilityCode === ResponsibilityTypeCodesEnum.RSP_TYPE_II ||
                currentResponsibilityCode === ResponsibilityTypeCodesEnum.RSP_TYPE_III) && (
                <Row title="Срок">
                  <TextInputField
                    id={`${responsibleEntityId}.punishmentTerm`}
                    className={styles.PunishmentTermTextInput}
                    name={`${fieldName}.punishmentTerm`}
                    placeholder="Введите срок"
                    disabled={isWatchOnly}
                  />
                  <KnmLateFieldsRemark
                    lateFieldPath={`${lateFieldName}/${fieldValue.listId}/punishmentTerm`}
                    fieldId={`${responsibleEntityId}.punishmentTerm`}
                    isLeftPosition={true}
                  />
                </Row>
              )}

              <FieldBlock
                label={
                  <>
                    Структурные единицы нормативных правовых актов, содержащих информацию
                    <br />о мерах ответственности контролируемых лиц
                    <KnmLateFieldsRemark
                      lateFieldPath={`${lateFieldName}/${fieldValue.listId}/structuresNPA`}
                      fieldId={`${responsibleEntityId}.structuresNPA`}
                    />
                  </>
                }
                button={
                  <Button
                    className="shared-btn-add"
                    text="Добавить"
                    type="button"
                    variant="link"
                    color="primary"
                    disabled={isWatchOnly}
                    onClick={addField(`${fieldName.slice(fieldName.indexOf('.') + 1)}.structuresNPA`)}
                  />
                }
                id={`${responsibleEntityId}.structuresNPA`}
              >
                <FieldArray name={`${fieldName}.structuresNPA`}>
                  {({
                    fields: structuresNpaFields,
                  }: FieldArrayRenderProps<IKnmErknmFormStructuresNPA, HTMLElement>) => {
                    if (!structuresNpaFields.length) {
                      return null;
                    }

                    return (
                      <Row>
                        {structuresNpaFields.map((structuresFieldName, structuresIndex) => {
                          return (
                            <Row key={id} isListItem>
                              <TextareaField
                                className="shared-deleting-row"
                                id={`${structuresFieldName}.value`}
                                name={`${structuresFieldName}.value`}
                                placeholder="Введите значение"
                                resize="none"
                                disabled={isWatchOnly}
                              />
                              <CloseButton
                                className="shared-deleting-row-btn"
                                onClick={makeRemoveField(
                                  `${fieldName.slice(fieldName.indexOf('.') + 1)}.structuresNPA`,
                                  structuresIndex,
                                )}
                              />
                              <KnmLateFieldsRemark
                                lateFieldPath={`${lateFieldName}/${fieldValue.listId}/structuresNPA/value`}
                                fieldId={`${structuresFieldName}.value`}
                                isLeftPosition={true}
                              />
                            </Row>
                          );
                        })}
                      </Row>
                    );
                  }}
                </FieldArray>
              </FieldBlock>
            </Row>
          );
        });
      }}
    </FieldArray>
  );
};

const mapStateToProps = (state: IRootState): IErknmResultDecisionsResponsibleEntitiesStoreProps => ({
  settings: getAdminFetchSettings(state),
  created: getKnmDataCreated(state),
});

export const ErknmResultDecisionsResponsibleEntities = connect(mapStateToProps)(
  ErknmResultDecisionsResponsibleEntitiesWithoutStore,
);
